import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useNotify, useTranslate } from 'react-admin';
import { alpha } from '@material-ui/core/styles';
import { CRM_COLORS } from '../../theme/crmColors';
import AddPrestataireDialog from './AddPrestataireDialog';
import EditPrestataireDialog from './EditPrestataireDialog';
import {
    composeHomologationPdfUrl,
    deletePrestataireAffiliation,
    fetchPrestataireAffiliations,
    flattenAffiliationsToRows,
} from './api';
import { PrestataireListRow } from './types';

const useStyles = makeStyles(() => ({
    root: {
        display: 'grid',
        gap: 16,
        minWidth: 0,
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
    },
    actions: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    paper: {
        overflowX: 'auto',
        border: `1px solid ${CRM_COLORS.border}`,
        boxShadow: CRM_COLORS.shadow,
    },
    headCell: {
        fontWeight: 600,
        color: CRM_COLORS.secondary,
        whiteSpace: 'nowrap',
    },
    empty: {
        padding: 32,
        textAlign: 'center',
        color: CRM_COLORS.text.secondary,
    },
    iconButton: {
        color: CRM_COLORS.primary,
        '&:hover': {
            backgroundColor: alpha(CRM_COLORS.primary, 0.08),
        },
    },
    dangerButton: {
        color: CRM_COLORS.error,
        '&:hover': {
            backgroundColor: alpha(CRM_COLORS.error, 0.08),
        },
    },
}));

const PrestatairesList: React.FC = () => {
    const classes = useStyles();
    const translate = useTranslate();
    const notify = useNotify();

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<PrestataireListRow[]>([]);
    const [addOpen, setAddOpen] = useState(false);
    const [editRow, setEditRow] = useState<PrestataireListRow | null>(null);
    const [unlinkingId, setUnlinkingId] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const affiliations = await fetchPrestataireAffiliations();
            setRows(flattenAffiliationsToRows(affiliations));
        } catch (error) {
            const message = error instanceof Error ? error.message : translate('app.prestataires.messages.error');
            notify(message, 'error');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [notify, translate]);

    useEffect(() => {
        load();
    }, [load]);

    const typeLabel = useCallback(
        (types: string[]) =>
            types.map((type) => translate(`app.prestataires.types.${type}`, { _: type })).join(', '),
        [translate]
    );

    const scopesLabel = useCallback(
        (scopes: PrestataireListRow['scopes']) =>
            (scopes || [])
                .map((scope) => {
                    const typeName = translate(`app.prestataires.types.${scope.type}`, { _: scope.type });
                    const codes = (scope.departmentCodes || []).join(', ');
                    return codes ? `${typeName} : ${codes}` : typeName;
                })
                .join(' · '),
        [translate]
    );

    const handleViewPdf = (row: PrestataireListRow) => {
        if (!row.homologationMedia) {
            notify(translate('app.prestataires.messages.pdfUnavailable'), 'warning');
            return;
        }
        const url = composeHomologationPdfUrl(row.homologationMedia);
        if (!url) {
            notify(translate('app.prestataires.messages.pdfUnavailable'), 'warning');
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleUnlink = async (row: PrestataireListRow) => {
        const confirmed = window.confirm(translate('app.prestataires.messages.confirmUnlink'));
        if (!confirmed) {
            return;
        }
        setUnlinkingId(row.affiliationId);
        try {
            await deletePrestataireAffiliation(row.affiliationId);
            notify(translate('app.prestataires.messages.unlinkSuccess'), 'success');
            await load();
        } catch (error) {
            const message = error instanceof Error ? error.message : translate('app.prestataires.messages.error');
            notify(message, 'error');
        } finally {
            setUnlinkingId(null);
        }
    };

    return (
        <Box className={classes.root}>
            <Box className={classes.toolbar}>
                <Typography variant='body2' color='textSecondary'>
                    {translate('app.prestataires.list.summary', {
                        companies: rows.length,
                    })}
                </Typography>
                <Box className={classes.actions}>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={load}
                        disabled={loading}
                        variant='outlined'
                        color='primary'
                    >
                        {translate('app.prestataires.actions.refresh')}
                    </Button>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => setAddOpen(true)}
                        variant='contained'
                        color='primary'
                    >
                        {translate('app.prestataires.actions.add')}
                    </Button>
                </Box>
            </Box>

            <Paper className={classes.paper}>
                {loading ? (
                    <Box className={classes.empty}>
                        <CircularProgress size={28} />
                    </Box>
                ) : rows.length === 0 ? (
                    <Box className={classes.empty}>{translate('app.prestataires.list.empty')}</Box>
                ) : (
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headCell}>
                                    {translate('app.prestataires.columns.companyName')}
                                </TableCell>
                                <TableCell className={classes.headCell}>
                                    {translate('app.prestataires.columns.types')}
                                </TableCell>
                                <TableCell className={classes.headCell}>
                                    {translate('app.prestataires.columns.departments')}
                                </TableCell>
                                <TableCell className={classes.headCell}>
                                    {translate('app.prestataires.columns.email')}
                                </TableCell>
                                <TableCell className={classes.headCell} align='right'>
                                    {translate('app.prestataires.columns.actions')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.key} hover>
                                    <TableCell>{row.companyName || '—'}</TableCell>
                                    <TableCell>{typeLabel(row.types) || '—'}</TableCell>
                                    <TableCell>{scopesLabel(row.scopes) || '—'}</TableCell>
                                    <TableCell>{row.companyEmail || '—'}</TableCell>
                                    <TableCell align='right'>
                                        <Tooltip
                                            title={translate(
                                                row.homologationMedia
                                                    ? 'app.prestataires.actions.viewPdf'
                                                    : 'app.prestataires.messages.pdfUnavailable',
                                            )}
                                        >
                                            <span>
                                                <IconButton
                                                    size='small'
                                                    className={classes.iconButton}
                                                    disabled={!row.homologationMedia}
                                                    onClick={() => handleViewPdf(row)}
                                                >
                                                    <PictureAsPdfIcon fontSize='small' />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={translate('app.prestataires.actions.edit')}>
                                            <IconButton
                                                size='small'
                                                className={classes.iconButton}
                                                onClick={() => setEditRow(row)}
                                            >
                                                <EditIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={translate('app.prestataires.actions.unlink')}>
                                            <span>
                                                <IconButton
                                                    size='small'
                                                    className={classes.dangerButton}
                                                    disabled={unlinkingId === row.affiliationId}
                                                    onClick={() => handleUnlink(row)}
                                                >
                                                    {unlinkingId === row.affiliationId ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <DeleteOutlineIcon fontSize='small' />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            <AddPrestataireDialog open={addOpen} onClose={() => setAddOpen(false)} onSuccess={load} />
            <EditPrestataireDialog
                open={Boolean(editRow)}
                row={editRow}
                onClose={() => setEditRow(null)}
                onSuccess={load}
            />
        </Box>
    );
};

export default PrestatairesList;
